import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';

type LimitMode = 'blocked' | 'number' | 'unlimited';

interface LimitControlProps {
  /** null = unlimited, 0 = not allowed. */
  value: number | null;
  onChange: (value: number | null) => void;
  /** Hide "Not allowed" when 0 makes no sense (e.g. funds in a portfolio). */
  allowBlocked?: boolean;
  ariaLabel: string;
  disabled?: boolean;
}

const modeOf = (value: number | null): LimitMode => {
  if (value === null) {
    return 'unlimited';
  }

  return value === 0 ? 'blocked' : 'number';
};

/**
 * Three clear choices instead of magic numbers: Not allowed · Up to [n] · Unlimited.
 * The number box only matters when "Up to" is chosen; typing in it selects "Up to".
 */
export function LimitControl({ value, onChange, allowBlocked = true, ariaLabel, disabled }: LimitControlProps) {
  const mode = modeOf(value);
  const [text, setText] = useState(mode === 'number' ? String(value) : '');

  useEffect(() => {
    if (mode === 'number') {
      setText(String(value));
    }
  }, [value, mode]);

  const handleMode = (event: React.MouseEvent<HTMLElement>, next: LimitMode | null) => {
    if (!next) {
      return;
    }

    if (next === 'blocked') {
      onChange(0);
    } else if (next === 'unlimited') {
      onChange(null);
    } else {
      onChange(Math.max(1, Number(text) || 1));
    }
  };

  const handleNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
    setText(event.target.value);

    const number = parseInt(event.target.value, 10);

    if (Number.isInteger(number) && number >= 1) {
      onChange(number);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={mode}
        onChange={handleMode}
        aria-label={ariaLabel}
        disabled={disabled}
      >
        {allowBlocked && (
          <ToggleButton value="blocked" sx={{ textTransform: 'none', gap: '4px' }}>
            <LockOutlinedIcon fontSize="inherit" /> Not allowed
          </ToggleButton>
        )}
        <ToggleButton value="number" sx={{ textTransform: 'none' }}>
          Up to
        </ToggleButton>
        <ToggleButton value="unlimited" sx={{ textTransform: 'none', gap: '4px' }}>
          <AllInclusiveIcon fontSize="inherit" /> Unlimited
        </ToggleButton>
      </ToggleButtonGroup>
      {mode === 'number' && (
        <TextField
          size="small"
          type="number"
          value={text}
          onChange={handleNumber}
          inputProps={{ min: 1, 'aria-label': `${ariaLabel}: how many` }}
          sx={{ width: '88px' }}
          disabled={disabled}
        />
      )}
    </Box>
  );
}
